import { Bars2Icon, CheckIcon, ChevronRightIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { isNumber } from 'radash';
import { useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel/ButtonPanel';
import { Checkbox } from '@/components/core/Checkbox';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeaderDefault } from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { LOCAL_STORAGE } from '@/constants';
import { useCRUDView } from '@/hooks/useCRUDView';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';

const ItemEditorSettings = () => {

    const [open, setOpen] = useState(false);
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [openConfirmationDeleteViewDialog, setOpenConfirmationDeleteViewDialog] = useState<number | undefined>(undefined);
    const { toggleAddView, getAllViews, setCurrentView, selectedView, setOnEditView, onDeleteView, setCloseOtherPanels, closeOtherPanels} = useCRUDView();
    const { user } = useUser();
    const { t } = useTranslation();
    const [isDeleteConfirmationDisabled, setIsDeleteConfirmationDisabled] =
        useLocalStorage<boolean>(
            `${LOCAL_STORAGE.DISABLE_DELETE_CONFIRMATION}${user?.id}`,
            false,
        );
    const allViews = getAllViews();
    const toggleSubList = (index: number | null) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const onDisabledConfirmation = (disableDialog: boolean) => {
        setIsDeleteConfirmationDisabled(disableDialog);
    };

    const onCloseOtherPanels = (closeOtherPanels: boolean) => {
        sessionStorage.setItem('closeOtherPanels', `${closeOtherPanels}`);
        setCloseOtherPanels(closeOtherPanels);
    };

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <ButtonPanel icon={Bars2Icon} isDisabled={false} />
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    sideOffset={4}
                    className="tw-w-60 tw-z-50 tw-rounded tw-bg-white tw-p-0 tw-shadow"
                >
                    <div className="tw-font-bold tw-w-full tw-bg-gray-100 tw-p-2 tw-flex tw-items-center tw-justify-between tw-cursor-pointer">
                        {t('ITEMSETTINGS.TITLE')}
                        <IconButton icon={XMarkIcon}
                            onClick={() => {
                                setOpen(false);
                            }}
                            className='tw-mr-[-3px]'
                            size='sm'/>
                    </div>
                    <ul className="tw-space-y-1 tw-list-none tw-p-0">
                        <li>
                            <div
                                onClick={() => toggleSubList(1)}
                                className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-p-2"
                            >
                                <span className="tw-font-bold">View</span>
                                <span className={`tw-transform tw-transition-transform ${openIndex === 1 ? 'tw-rotate-90' : ''}`}>
                                    <Icon icon={ChevronRightIcon} size='sm' />
                                </span>
                            </div>
                            {openIndex === 1 && (
                                <ul className="tw-pl-6 tw-mt-2 tw-space-y-2">
                                    <li className="tw-list-none tw-cursor-pointer" onClick={toggleAddView}>{t('ITEMSETTINGS.NEW_VIEW')}</li>
                                </ul>
                            )}
                        </li>

                        <li>
                            <div
                                onClick={() => toggleSubList(2)}
                                className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-p-2"
                            >
                                <span className="tw-font-bold">{t('ITEMSETTINGS.SELECT_VIEW')}</span>
                                <span className={`tw-transform tw-transition-transform ${openIndex === 2 ? 'tw-rotate-90' : ''}`}>
                                    <Icon icon={ChevronRightIcon} size='sm' />
                                </span>
                            </div>
                            {openIndex === 2 && (
                                <ul className="tw-pl-6 tw-mt-2 tw-space-y-2">
                                    <li className="tw-list-none tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-pr-2 tw-pb-2" onClick={() => { setCurrentView(undefined); }}>
                                        Default (no view)
                                        {!selectedView && <Icon icon={CheckIcon} size='sm' />}
                                    </li>
                                    {allViews.map((view: any, index: number) => view.name ? (
                                        <li key={index} className="tw-list-none tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-pr-2 tw-pb-2"
                                            onClick={() => { setCurrentView(index); }}>
                                            <span className="tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-max-w-[85%]">
                                                {view.name}
                                            </span>
                                            {(JSON.stringify(selectedView) === JSON.stringify(view)) && <Icon icon={CheckIcon} size='sm' />}
                                        </li>
                                    ) : null)}
                                </ul>
                            )}
                        </li>
                        {(allViews.length > 0) && <li>
                            <div
                                onClick={() => toggleSubList(3)}
                                className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-p-2"
                            >
                                <span className="tw-font-bold">{t('ITEMSETTINGS.EDIT_VIEW')}</span>
                                <span className={`tw-transform tw-transition-transform ${openIndex === 3 ? 'tw-rotate-90' : ''}`}>
                                    <Icon icon={ChevronRightIcon} size='sm' />
                                </span>
                            </div>
                            {openIndex === 3 && (
                                <ul className="tw-pl-6 tw-mt-2 tw-space-y-2">
                                    {allViews.map((view: any, i: number) => (
                                        <li key={i} className="tw-list-none tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-pr-1" onClick={() => setOnEditView(i)}>
                                            <span className="tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis tw-max-w-[85%]">
                                                {view.name}
                                            </span>
                                            <IconButton icon={TrashIcon} size='sm' variant='destructive' type="button" onClick={() => setOpenConfirmationDeleteViewDialog(i)} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>}
                        <span className='tw-divide-x-2 tw-block tw-h-[1px] tw-bg-gray-300 tw-rounded-sm tw-w-full'></span>
                        <li>
                            <div
                                className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-px-0"
                            >
                                <span className='tw-pl-2'>{t('CLOSE_OTHER_PANELS')}</span>
                                <Checkbox className='tw-m-[10px]' id='close-panels' isChecked={closeOtherPanels} onCheckedChange={(pressed: any) => onCloseOtherPanels(pressed)}/>
                            </div>
                        </li>
                        <span className='tw-divide-x-2 tw-block tw-h-[1px] tw-bg-gray-300 tw-rounded-sm tw-w-full'></span>
                        <li>
                            <div
                                className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-px-0"
                            >
                                <span className='tw-pl-2'>{t('EDITOR.DISABLE_DELETE_CONFIRMATION')}</span>
                                <Checkbox className='tw-m-[10px]' isChecked={isDeleteConfirmationDisabled} onCheckedChange={(pressed: any) => onDisabledConfirmation(pressed)}/>
                            </div>
                        </li>
                    </ul>
                </PopoverContent>
            </Popover>
            <Dialog open={isNumber(openConfirmationDeleteViewDialog)}>
                <DialogContent variant="small" className='tw-p-4'>
                    <DialogHeaderDefault>
                        {t('VIEW.CONFIRM_DELETE_VIEW')}
                     </DialogHeaderDefault>
                    <DialogFooter>
                        <Button variant="default" onClick={() => {
                            if (isNumber(openConfirmationDeleteViewDialog)) {
                                onDeleteView(openConfirmationDeleteViewDialog);
                            }
                            setOpenConfirmationDeleteViewDialog(undefined);
                        }}>{t('ACTION.DELETE')}</Button>
                        <DialogClose asChild={true}>
                            <Button variant="secondary" type="submit">{t('CANCEL')}</Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export { ItemEditorSettings };
